import { useCallback, useEffect, useRef, useState } from 'react';

import { ListValue } from '@xemplo/stateful-list-core';

/**
 * Persistent value→label store for a single-select's chosen option — the single-select
 * analogue of `useValueTracker`'s selected-items cache in `@xemplo/base-multi-select`.
 *
 * The dropdown's one input doubles as search box and value display, and the option list is
 * replaced on every server-search keystroke. Deriving the selected label from that churning
 * list loses it; this hook caches the label the first time it can be resolved (from a list
 * hit, a selection, or a consumer-supplied `seedLabel` for prefill) and keeps returning it
 * regardless of what the list does afterwards.
 *
 * `label` is the current value's resolved label as reactive state, so an async-arriving
 * option re-renders the consumer; `capture` records a label the caller already holds (a
 * selection), so it survives even though that option leaves the filtered results.
 */
export function useSelectedLabel(
  value: ListValue,
  getItemLabel: (value: ListValue) => string,
  seedLabel?: string,
) {
  const cache = useRef<Map<ListValue, string>>(new Map());
  const [label, setLabel] = useState('');

  const capture = useCallback((next: ListValue, nextLabel: string) => {
    if (next && nextLabel) cache.current.set(next, nextLabel);
  }, []);

  const resolve = useCallback((next: ListValue) => cache.current.get(next) ?? '', []);

  // Re-runs when the value changes or the list can newly resolve it (getItemLabel's
  // identity tracks the items). A fresh list hit refreshes the cache; otherwise the cached
  // (or seeded) label stands, so server-search churn never blanks it.
  useEffect(() => {
    if (!value) {
      setLabel('');
      return;
    }

    const fromItems = getItemLabel(value);
    if (fromItems) cache.current.set(value, fromItems);
    else if (seedLabel && !cache.current.has(value)) cache.current.set(value, seedLabel);

    setLabel(cache.current.get(value) ?? '');
  }, [value, getItemLabel, seedLabel]);

  return { label, resolve, capture };
}
